import React, { Fragment, useState, useContext, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import GetProductById from "../Merchant/lib/getProductById";
import ProductDetailSkeleton from "../components/ProductDetailSkeleton";
import { CartContext } from "../../../Context/CartContext";
import { AuthContext } from "../../../Context/AuthContext";
import { useWishlistCheck, useToggleWishlist } from "../libs/useWishlist";
import { useRecordRecentlyViewed } from "../libs/useRecentlyViewed";
import useProductReviews from "../libs/useProductReviews";
import { usePendingReviews } from "../libs/useReviews";
import { getProductSizes, getProductStockAvailable } from "../libs/productSizes";
import { getSizeFieldsForCategory } from "../../../dashboard/merchant/utils/categoryFormConfig";
import useMarketplaceShippingFees, {
  getShippingCostFromSettings,
  mergeShippingSettings,
} from "../libs/useMarketplaceShippingFees";
import { notifyAddToCartResult } from "../libs/cartAddHelpers";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import { getProductUrl } from "../libs/getProductUrl";
import { getCategoryPath } from "../libs/categoryRoutes";
import { getMerchantStorePath } from "../libs/marketplaceRoutes";
import useRelatedProducts from "../libs/useRelatedProducts";
import useQuickAddToCart from "../libs/useQuickAddToCart";
import ProductGrid from "../components/ProductGrid";
import { PdpRelatedSkeleton, PdpReviewsSkeleton } from "../components/PdpSectionSkeletons";
import QuickAddSizeModal from "../components/QuickAddSizeModal";
import { getProductSpecEntries } from "../../../dashboard/merchant/utils/categoryFormConfig";
import ProductPriceDisplay, { ProductDiscountBadge } from "../components/ProductPriceDisplay";
import StarRating from "../components/StarRating";
import "../ProductCategory/ProductCategory.css";
import "../components/ProductCard.css";
import "./AllProductDetail.css";

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const AllProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = GetProductById(slug);
  const productId = data?.product?._id;

  const { data: reviewsData, isLoading: reviewsLoading } = useProductReviews(productId);
  const { data: relatedProducts = [], isLoading: relatedLoading } = useRelatedProducts(productId);
  const { isLoggedIn } = useContext(AuthContext);
  const { data: isSaved } = useWishlistCheck(productId);
  const { data: pendingReviews = [] } = usePendingReviews();
  const { mutate: toggleWishlist } = useToggleWishlist();
  const { mutate: recordView } = useRecordRecentlyViewed();
  const [topImageIndex, setTopImageIndex] = useState(0);
  const [descriptionVisible, setDescriptionVisible] = useState(false);
  const { addItemToCart } = useContext(CartContext);
  const { quickAdd, pending, confirmSize, cancelSize } = useQuickAddToCart(addItemToCart);
  const [shippingPickupOverlay, setShippingPickupOverlay] = useState(false);
  const [shippingDoorOverlay, setShippingDoorOverlay] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const { data: shippingSettings } = useMarketplaceShippingFees();
  const shipping = mergeShippingSettings(shippingSettings);
  const pickupFee = getShippingCostFromSettings(shipping, "pickup");
  const doorFee = getShippingCostFromSettings(shipping, "door");

  const canReview = pendingReviews.some(
    (p) => (p.product?._id || p.product)?.toString() === productId
  );

  useEffect(() => {
    const loadedProduct = data?.product;
    if (!loadedProduct?.slug || !slug) return;
    if (OBJECT_ID_PATTERN.test(slug) && loadedProduct.slug !== slug) {
      navigate(getProductUrl(loadedProduct), { replace: true });
    }
  }, [data, slug, navigate]);

  useEffect(() => {
    if (isLoggedIn && productId) {
      recordView(productId);
    }
  }, [productId, isLoggedIn, recordView]);

  const handleWishlist = () => {
    if (!isLoggedIn) {
      toast.info("Please log in to save items");
      return;
    }
    toggleWishlist(
      { productId, saved: isSaved },
      {
        onSuccess: () => toast.success(isSaved ? "Removed from saved items" : "Saved to wishlist"),
      }
    );
  };

  const handleAddToCart = (product) => {
    const sizeFields = getSizeFieldsForCategory(product.category);
    const sizes = getProductSizes(product, sizeFields);
    if (sizes.length && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    const stockForLine = getProductStockAvailable(product, selectedSize || null, sizeFields);
    const result = addItemToCart(
      { ...product, selectedSize: selectedSize || null, stockAvailable: stockForLine },
      quantity
    );
    notifyAddToCartResult(result);
    return result;
  };

  if (isError) {
    return (
      <Fragment>
        <MarketNavbar />
        <MarketplacePage className="marketplace-page--pdp">
          <div className="all-product-detail product-detail-error">
            <h2>Product not found</h2>
            <p>This product may have been removed or the link is invalid.</p>
            <Link to="/market">Back to marketplace</Link>
          </div>
        </MarketplacePage>
      </Fragment>
    );
  }

  if (isLoading || !data || !data.product) {
    return (
      <Fragment>
        <MarketNavbar />
        <MarketplacePage className="marketplace-page--pdp">
          <ProductDetailSkeleton />
        </MarketplacePage>
      </Fragment>
    );
  }

  const product = data.product;
  const seller = data.seller;
  const storeActivities = data.storeActivities;
  const sizeFields = getSizeFieldsForCategory(product.category);
  const sizes = getProductSizes(product, sizeFields);
  const hasSizes = sizes.length > 0;
  const maxQty = hasSizes
    ? selectedSize
      ? getProductStockAvailable(product, selectedSize, sizeFields)
      : 0
    : getProductStockAvailable(product, null, sizeFields);
  const specEntries = getProductSpecEntries(product);
  const topImage = product.images?.[topImageIndex] || product.images?.[0];

  const handleBuyNow = () => {
    const result = handleAddToCart(product);
    if (result?.success) {
      navigate("/market/checkout");
    }
  };

  return (
    <Fragment>
      <MarketNavbar />
      <MarketplacePage className="marketplace-page--pdp">
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/market">Home</Link>
        <span>/</span>
        {product.category && (
          <>
            <Link to={getCategoryPath(product.category)}>{product.category}</Link>
            <span>/</span>
          </>
        )}
        <span className="pdp-breadcrumb-current">{product.name}</span>
      </nav>
      <div className="all-product-detail">
        <div className="wrapper">
          <div className="image">
            <span>
              <div className="cascaded-images-container">
                {product.images?.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`pdp-thumb ${index === topImageIndex ? "pdp-thumb--active" : ""}`}
                    onClick={() => setTopImageIndex(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  </button>
                ))}
              </div>
              <div className="top-image">
                <ProductDiscountBadge product={product} className="pdp-discount-badge" />
                <img src={topImage} alt="Main Product" />
              </div>
            </span>
          </div>

          <div className="all-product-info">
            <div className="wrapper pdp-main">
              <h3 className="pdp-title">{product.name}</h3>

              <div className="pdp-meta">
                {product.category && (
                  <span className="pdp-meta-chip">{product.category}</span>
                )}
                {product.brand && (
                  <span className="pdp-meta-chip pdp-meta-chip--muted">Brand: {product.brand}</span>
                )}
                {product.creator && (
                  <Link
                    to={getMerchantStorePath(product.creator._id || product.creator)}
                    className="pdp-meta-chip pdp-meta-chip--link"
                  >
                    View seller
                  </Link>
                )}
                {product.color && (
                  <span className="pdp-meta-chip pdp-meta-chip--muted">Color: {product.color}</span>
                )}
              </div>

              {specEntries.length > 0 && (
                <div className="pdp-specs">
                  <h4 className="pdp-specs__title">Product details</h4>
                  <dl className="pdp-specs__list">
                    {specEntries.map(({ key, label, value }) => (
                      <div key={key} className="pdp-specs__row">
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <ProductPriceDisplay product={product} size="pdp" className="pdp-price-block" />

              {hasSizes && !selectedSize ? (
                <span className="pdp-stock-badge pdp-stock-badge--muted">Select a size to see stock</span>
              ) : maxQty <= 0 ? (
                <span className="pdp-stock-badge pdp-stock-badge--out">Sold out</span>
              ) : maxQty <= 5 ? (
                <span className="pdp-stock-badge pdp-stock-badge--low">Only {maxQty} left in stock</span>
              ) : (
                <span className="pdp-stock-badge">{maxQty} in stock</span>
              )}

              {hasSizes && (
                <div className="pdp-size-picker">
                  <p className="pdp-field-label">Size</p>
                  <div className="pdp-size-options">
                    {sizes.map((s) => (
                      <button
                        key={s.field}
                        type="button"
                        className={selectedSize === s.label ? "selected" : ""}
                        onClick={() => {
                          setSelectedSize(s.label);
                          setQuantity(1);
                        }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pdp-qty-picker">
                <p className="pdp-field-label">Quantity</p>
                <div className="pdp-qty-controls">
                  <button
                    type="button"
                    className="pdp-qty-btn"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || maxQty <= 0}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <span className="pdp-qty-value" aria-live="polite">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    className="pdp-qty-btn"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    disabled={quantity >= maxQty || maxQty <= 0}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="pdp-actions">
                <button
                  type="button"
                  className="pdp-add-cart-btn"
                  onClick={() => handleAddToCart(product)}
                  disabled={maxQty <= 0 || (hasSizes && !selectedSize)}
                >
                  {maxQty <= 0 ? "Sold out" : "Add to cart"}
                </button>
                <button
                  type="button"
                  className="pdp-buy-now-btn"
                  onClick={handleBuyNow}
                  disabled={maxQty <= 0 || (hasSizes && !selectedSize)}
                >
                  Buy now
                </button>
                <button
                  type="button"
                  className="pdp-wishlist-btn"
                  onClick={handleWishlist}
                  aria-label="Save item"
                >
                  {isSaved ? <AiFillHeart color="#d0300e" /> : <AiOutlineHeart />}
                </button>
              </div>

              <div className="description">
                <button
                  type="button"
                  className="description-toggle"
                  onClick={() => setDescriptionVisible(!descriptionVisible)}
                  aria-expanded={descriptionVisible}
                >
                  <span>Description</span>
                  <i className={`fa-solid fa-chevron-${descriptionVisible ? "up" : "down"}`} />
                </button>
                {descriptionVisible && (
                  <p className="description-body">{product.description}</p>
                )}
              </div>
            </div>
            <div className="shipping pdp-shipping">
              <h3 className="pdp-shipping-title">{shipping.sectionTitle}</h3>
              <div className="shipping-wrapper">
                <i className="fa-solid fa-hands-holding-circle" />
                <div className="info">
                  <h3>{shipping.pickupLabel}</h3>
                  <p>{shipping.deliveryFeeLabel} ₦ {pickupFee.toLocaleString()}</p>
                  <p>{shipping.pickupEstimate}</p>
                  <button type="button" onClick={() => setShippingPickupOverlay(true)}>
                    {shipping.detailsButtonLabel}
                  </button>
                </div>
              </div>
              {shippingPickupOverlay && (
                <div className="shipping-detail-overlay">
                  <div className="shipping-detail-overlay-card">
                    <span>
                      <h3>{shipping.overlayTitle}</h3>
                      <button type="button" onClick={() => setShippingPickupOverlay(false)}>Ok</button>
                    </span>
                    <h3>{shipping.pickupLabel.toUpperCase()}</h3>
                    <p>{shipping.pickupDetailsText}</p>
                    <h4>{shipping.overlayFeeLabel.toUpperCase()}</h4>
                    <div className="wrapper">
                      <p>{shipping.overlayFeeLabel}</p>
                      <h3>₦{pickupFee.toLocaleString()}</h3>
                    </div>
                  </div>
                </div>
              )}
              {shippingDoorOverlay && (
                <div className="shipping-detail-overlay">
                  <div className="shipping-detail-overlay-card">
                    <span>
                      <h3>{shipping.overlayTitle}</h3>
                      <button type="button" onClick={() => setShippingDoorOverlay(false)}>Ok</button>
                    </span>
                    <h3>{shipping.doorLabel.toUpperCase()}</h3>
                    <p>{shipping.doorDetailsText}</p>
                    <h4>{shipping.overlayFeeLabel.toUpperCase()}</h4>
                    <div className="wrapper">
                      <p>{shipping.overlayFeeLabel}</p>
                      <h3>₦{doorFee.toLocaleString()}</h3>
                    </div>
                  </div>
                </div>
              )}
              <div className="shipping-wrapper">
                <i className="fa-solid fa-truck-moving" />
                <div className="info">
                  <h3>{shipping.doorLabel}</h3>
                  <p>{shipping.deliveryFeeLabel} ₦ {doorFee.toLocaleString()}</p>
                  <p>{shipping.doorEstimate}</p>
                  <button type="button" onClick={() => setShippingDoorOverlay(true)}>
                    {shipping.detailsButtonLabel}
                  </button>
                </div>
              </div>
              <div className="shipping-wrapper">
                <i className="fa-solid fa-rotate-left" />
                <div className="info">
                  <h3>{shipping.returnPolicyTitle}</h3>
                  <p>{shipping.returnPolicyText}</p>
                  <Link to={shipping.returnPolicyLink}>{shipping.detailsButtonLabel}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {storeActivities && (
          <section className="pdp-seller-card dashboard-card" style={{ margin: "24px 0" }}>
            <h3 className="dashboard-card__title">Seller information</h3>
            <div className="pdp-seller-card__grid">
              <div>
                <strong>{storeActivities.businessName || [seller?.firstName, seller?.lastName].filter(Boolean).join(" ") || "Store"}</strong>
                {storeActivities.isVerified && (
                  <span className="dashboard-badge dashboard-badge--success" style={{ marginLeft: 8 }}>Verified</span>
                )}
                {(storeActivities.city || storeActivities.state) && (
                  <p style={{ margin: "6px 0", color: "var(--dash-muted)" }}>
                    {[storeActivities.city, storeActivities.state].filter(Boolean).join(", ")}
                  </p>
                )}
                {storeActivities.businessDescription && (
                  <p style={{ margin: "8px 0 0", lineHeight: 1.5 }}>{storeActivities.businessDescription}</p>
                )}
              </div>
              <div className="pdp-seller-card__stats">
                <div><span>Products</span><strong>{storeActivities.totalProducts ?? 0}</strong></div>
                <div><span>Orders fulfilled</span><strong>{storeActivities.totalOrders ?? 0}</strong></div>
                <div><span>Total sales</span><strong>NGN {(storeActivities.totalSales || 0).toLocaleString()}</strong></div>
                {storeActivities.reviewCount > 0 && (
                  <div>
                    <span>Store rating</span>
                    <StarRating rating={storeActivities.avgRating} count={storeActivities.reviewCount} size="sm" />
                  </div>
                )}
                {storeActivities.joinedAt && (
                  <div><span>Joined</span><strong>{new Date(storeActivities.joinedAt).toLocaleDateString()}</strong></div>
                )}
              </div>
            </div>
            {product.creator && (
              <Link
                to={getMerchantStorePath(product.creator._id || product.creator)}
                className="dashboard-btn dashboard-btn--secondary"
                style={{ marginTop: 12, display: "inline-flex" }}
              >
                Visit store
              </Link>
            )}
          </section>
        )}
        {(relatedLoading || relatedProducts.length > 0) && (
          <section
            className="pdp-related"
            aria-busy={relatedLoading}
            aria-label="Related products"
          >
            <h3>Related products</h3>
            {relatedLoading ? (
              <PdpRelatedSkeleton count={4} />
            ) : (
              <ProductGrid products={relatedProducts} onAddToCart={quickAdd} />
            )}
          </section>
        )}
        <section className="customer-review pdp-reviews" aria-busy={reviewsLoading} aria-label="Customer reviews">
          <h3>Customer reviews</h3>
          {reviewsLoading ? (
            <PdpReviewsSkeleton />
          ) : (
            <>
              {reviewsData?.totalReviews ? (
                <p className="review-summary">
                  <StarRating rating={reviewsData.avgRating} count={reviewsData.totalReviews} size="lg" />
                </p>
              ) : (
                <div className="pdp-reviews-empty">
                  <p>No reviews yet</p>
                  <span>Purchase this item to share your experience.</span>
                </div>
              )}
              {canReview && (
                <Link to="/account/reviews" className="write-review-link">
                  Write a review
                </Link>
              )}
              {(reviewsData?.reviews || []).length > 0 && (
                <ul className="review-list">
                  {(reviewsData?.reviews || []).map((r) => (
                    <li key={r._id}>
                      <strong>
                        {r.user?.first_name} {r.user?.last_name}
                      </strong>
                      <StarRating rating={r.rating} showCount={false} size="sm" />
                      <p>{r.comment}</p>
                      <small>{new Date(r.createdAt).toLocaleDateString()}</small>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </div>
      </MarketplacePage>
      <QuickAddSizeModal pending={pending} onConfirm={confirmSize} onCancel={cancelSize} />
      <ToastContainer position="top-right" className="toast" />
    </Fragment>
  );
};

export default AllProductDetail;
